"use client";

import Link from "next/link";
import {
  ArrowRight,
  Bot,
  FileBarChart2,
  HandHelping,
  PlayCircle,
  Sparkles,
  Users,
} from "lucide-react";
import { FounderInsight } from "~/components/siteglue/blog/founder-insight";
import { DemoCTA, PrimaryCTA, TrishCTA } from "~/components/siteglue/siteglue-cta";
import { marketingAppDashboardUrl } from "~/lib/auth-paths";
import { MARKETING_BUILD_CUSTOM_NEW } from "~/lib/inbound-gpt/paths";
import { SITEGLUE_CTA_CONFIG, SITEGLUE_SIGNUP_CTA_LABEL, SITEGLUE_TRISH_ONBOARDING_URL, trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { cn } from "~/lib/utils";

export type BlogCalloutKind =
  | "hitl"
  | "opportunity"
  | "impact"
  | "employee"
  | "demo"
  | "founder";

const CALLOUT_CONFIG: Record<
  Exclude<BlogCalloutKind, "founder">,
  {
    eyebrow: string;
    Icon: typeof Sparkles;
    accent: string;
    defaultCta?: { label: string; href: string; event: string; useTrish?: boolean };
  }
> = {
  hitl: {
    eyebrow: "Always a Human-in-the-Loop™",
    Icon: HandHelping,
    accent: "border-[#4f81bd]/30 bg-[#4f81bd]/[0.06]",
  },
  opportunity: {
    eyebrow: "AI Opportunity Report",
    Icon: FileBarChart2,
    accent: "border-[#9cbb5a]/35 bg-[#9cbb5a]/[0.08]",
    defaultCta: {
      label: SITEGLUE_CTA_CONFIG.trish.label,
      href: SITEGLUE_TRISH_ONBOARDING_URL,
      event: "cta_trish_started",
      useTrish: true,
    },
  },
  impact: {
    eyebrow: "Monthly AI Impact Report",
    Icon: Sparkles,
    accent: "border-slate-200 bg-slate-50",
  },
  employee: {
    eyebrow: "AI Employee Recommendation",
    Icon: Bot,
    accent: "border-[#4f81bd]/25 bg-gradient-to-br from-white to-[#4f81bd]/[0.05]",
    defaultCta: {
      label: "Browse AI Employees",
      href: "/agents",
      event: "blog_employee_reco_clicked",
    },
  },
  demo: {
    eyebrow: "Live Demo",
    Icon: PlayCircle,
    accent: "border-[#4f81bd]/30 bg-[#4f81bd]/[0.05]",
  },
};

function plainTextFromChildren(children: React.ReactNode): string {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(plainTextFromChildren).join(" ").replace(/\s+/g, " ").trim();
  }
  return "";
}

export function BlogInlineCallout({
  kind,
  children,
  className,
  insight,
}: {
  kind: BlogCalloutKind;
  children?: React.ReactNode;
  className?: string;
  /** Preferred for `founder` callouts — plain text quote. */
  insight?: string;
}) {
  if (kind === "founder") {
    const fromChildren = plainTextFromChildren(children);
    return (
      <FounderInsight
        insight={insight?.trim() || fromChildren || undefined}
        className={cn("my-8", className)}
      />
    );
  }

  const config = CALLOUT_CONFIG[kind];
  const Icon = config.Icon;
  const buildHref = marketingAppDashboardUrl(MARKETING_BUILD_CUSTOM_NEW);

  return (
    <aside
      className={cn(
        "not-prose my-8 rounded-2xl border px-5 py-5 shadow-sm sm:px-6",
        config.accent,
        className,
      )}
      data-testid={`blog-callout-${kind}`}
    >
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
        <Icon className="h-4 w-4 text-[#4f81bd]" aria-hidden />
        {config.eyebrow}
      </div>
      <div className="text-[15px] leading-relaxed text-slate-700">{children}</div>
      <div className="mt-4 flex flex-wrap gap-2">
        {kind === "demo" ? (
          <DemoCTA location="blog_inline" size="sm" showArrow testId="blog-callout-demo-cta" />
        ) : null}
        {kind === "hitl" ? (
          <PrimaryCTA
            location="blog_inline"
            size="sm"
            label="Hire Your First AI Employee"
            href={buildHref}
            showArrow
            testId="blog-callout-hitl-cta"
          />
        ) : null}
        {kind === "opportunity" ? (
          <TrishCTA
            location="blog_inline_callout"
            size="sm"
            testId="blog-callout-trish-cta"
          />
        ) : null}
        {kind === "employee" || kind === "impact" ? (
          <Link
            href={config.defaultCta?.href ?? buildHref}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[#4f81bd] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-95"
            onClick={() => {
              trackSiteGlueEvent(config.defaultCta?.event ?? "blog_inline_cta_clicked", {
                callout_kind: kind,
                destination: config.defaultCta?.href ?? buildHref,
              });
            }}
          >
            {config.defaultCta?.label ?? SITEGLUE_SIGNUP_CTA_LABEL}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : null}
      </div>
    </aside>
  );
}

/** Shared icons for sidebar cards */
export const BlogSidebarIcons = {
  Users,
  Bot,
  FileBarChart2,
  Sparkles,
  HandHelping,
};
